// src/features/student-management/ui/organisms/PersonForm/hooks/usePersonFormNavigation.ts
import { useCallback, useRef, useState } from 'react';
import type { UseFormGetValues, UseFormReset, UseFormTrigger } from 'react-hook-form';

import type { Person } from '../../../../types/person';
import { STEP_TO_FIELDS, type StepKey } from '../personFormConfig';
import { extractErrorMessage, type StepSaveStatus, stepHasDirtyFields } from '../personFormHelpers';
import type { PersonFormValues } from '../personFormSchema';

interface UsePersonFormNavigationParams {
  readonly person: Person | undefined;
  readonly stepKeys: readonly StepKey[];
  readonly initialStep: StepKey | undefined;
  readonly trigger: UseFormTrigger<PersonFormValues>;
  readonly dirtyFields: Record<string, unknown>;
  readonly getValues: UseFormGetValues<PersonFormValues>;
  readonly reset: UseFormReset<PersonFormValues>;
  readonly onSaveStep:
    | ((params: {
        readonly step: string;
        readonly values: PersonFormValues;
        readonly dirtyFields: Record<string, unknown>;
      }) => Promise<void>)
    | undefined;
  readonly rolesSaveRef: React.MutableRefObject<(() => Promise<void>) | undefined>;
}

export interface UsePersonFormNavigationReturn {
  readonly currentStep: number;
  readonly maxReachedStep: number;
  readonly stepSaveStatus: StepSaveStatus;
  readonly stepSaveError: string | null;
  readonly goToStep: (index: number) => void;
  readonly handleNext: () => Promise<void>;
  readonly handlePrevious: () => void;
  readonly handleSaveRolesStep: () => Promise<void>;
}

/**
 * Gerencia navegação entre steps e o ciclo de salvamento por etapa (modo edição).
 *
 * Em modo criação (`!person`): valida os campos do step atual via `trigger` antes
 * de avançar e rastreia `maxReachedStep` para bloquear acesso antecipado no stepper.
 *
 * Em modo edição (`person`): salva campos sujos via `onSaveStep` antes de trocar de aba.
 */
export function usePersonFormNavigation({
  person,
  stepKeys: StepKeys,
  initialStep,
  trigger,
  dirtyFields,
  getValues,
  reset,
  onSaveStep,
  rolesSaveRef,
}: UsePersonFormNavigationParams): UsePersonFormNavigationReturn {
  const [currentStep, setCurrentStep] = useState(() => {
    if (!initialStep) return 0;
    const idx = StepKeys.indexOf(initialStep);
    return idx >= 0 ? idx : 0;
  });
  const [maxReachedStep, setMaxReachedStep] = useState(0);
  const [stepSaveStatus, setStepSaveStatus] = useState<StepSaveStatus>('idle');
  const [stepSaveError, setStepSaveError] = useState<string | null>(null);

  const onSaveStepRef = useRef(onSaveStep);
  onSaveStepRef.current = onSaveStep;

  const goToStep = useCallback((index: number) => {
    setCurrentStep(index);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const saveAndGoToStep = useCallback(
    async (index: number) => {
      const stepKey = StepKeys[currentStep] ?? 'personal';
      const dirty = dirtyFields;

      if (stepKey === 'roles' && person && rolesSaveRef.current) {
        setStepSaveStatus('loading');
        setStepSaveError(null);
        try {
          await rolesSaveRef.current();
          setStepSaveStatus('success');
          setTimeout(() => setStepSaveStatus('idle'), 2500);
          goToStep(index);
        } catch (err) {
          setStepSaveStatus('error');
          setStepSaveError(extractErrorMessage(err));
        }
        return;
      }

      if (person && onSaveStepRef.current && stepHasDirtyFields(stepKey, dirty)) {
        setStepSaveStatus('loading');
        setStepSaveError(null);
        try {
          await onSaveStepRef.current({
            step: stepKey,
            values: getValues(),
            dirtyFields: dirty,
          });
          reset(getValues());
          setStepSaveStatus('success');
          setTimeout(() => setStepSaveStatus('idle'), 2500);
          goToStep(index);
        } catch (err) {
          setStepSaveStatus('error');
          setStepSaveError(extractErrorMessage(err));
        }
        return;
      }

      goToStep(index);
    },
    [currentStep, person, getValues, dirtyFields, goToStep, StepKeys, reset, rolesSaveRef],
  );

  const handleNext = useCallback(async () => {
    if (!person) {
      const stepKey = StepKeys[currentStep] ?? 'personal';
      const fields = STEP_TO_FIELDS[stepKey] as Array<keyof PersonFormValues>;
      const valid = await trigger(fields);
      if (!valid) return;
      const nextStep = Math.min(currentStep + 1, StepKeys.length - 1);
      setMaxReachedStep(prev => Math.max(prev, nextStep));
      goToStep(nextStep);
      return;
    }
    void saveAndGoToStep(Math.min(currentStep + 1, StepKeys.length - 1));
  }, [person, currentStep, StepKeys, trigger, goToStep, saveAndGoToStep]);

  const handlePrevious = useCallback(() => {
    goToStep(Math.max(currentStep - 1, 0));
  }, [currentStep, goToStep]);

  const handleSaveRolesStep = useCallback(async () => {
    if (!rolesSaveRef.current) return;
    setStepSaveStatus('loading');
    setStepSaveError(null);
    try {
      await rolesSaveRef.current();
      setStepSaveStatus('success');
      setTimeout(() => setStepSaveStatus('idle'), 2500);
    } catch (err) {
      setStepSaveStatus('error');
      setStepSaveError(extractErrorMessage(err));
    }
  }, [rolesSaveRef]);

  return {
    currentStep,
    maxReachedStep,
    stepSaveStatus,
    stepSaveError,
    goToStep,
    handleNext,
    handlePrevious,
    handleSaveRolesStep,
  };
}
